import Table from "components/DataTable/Table";
import Spinner from "components/Spinner";
import { setNotification } from "features/Notification/notificationSlice";
import { useEffect, useState } from "react";
import { Dropdown } from "react-bootstrap";
import Modal from "react-bootstrap/Modal";
import { useDispatch, useSelector } from "react-redux";
import { Link, useSearchParams } from "react-router-dom";
import authHeader from "services/auth-header";
import API from "services/axios";
import GenerateAttendanceByDate from "./AttReports/GenerateAttendanceByDate";
import GenerateMonthlyAttendanceAllUsers from "./AttReports/GenerateMonthlyAttendanceAllUsers";
import UpdateAttUser from "./AttDetailsPage/UpdateAttUser";
import AttDeviceSummary from "./AttDeviceSummary";
import AttSelectDevice from "./AttSelectDevice";
import DeviceLogs from "./DeviceLogs";
import { getCurTimeOfDeviceTimeZone } from "utils/attendanceUtility";

export default function Attendance() {
  const [searchParams, setSearchParams] = useSearchParams();

  const dispatch = useDispatch();
  /*   ALL STATES
   ********************************************* */
  const [allAttendanceUsers, setAllAttendanceUsers] = useState(null);
  const [activeTab, setActiveTab] = useState("device");

  const [filterBy, setFilterBy] = useState("active");

  const [selectedDevice, setSelectedDevice] = useState(null);
  const [usersSummary, setUsersSummary] = useState({
    total: 0,
    admin: 0,
    user: 0,
  });

  const [showDevicesListModal, setShowDevicesListModal] = useState(false);
  const [userAddModal, setUserAddModal] = useState(false);

  const [usersLastSync, setUsersLastSync] = useState(null);
  const [logsLastSync, setLogsLastSync] = useState(null);

  const [loading, setLoading] = useState(false);

  const loadingOn = () => {
    setLoading(true);
  };

  const loadingOff = () => {
    setLoading(false);
  };

  /*   All Functions
   ********************************************* */

  // Sync-Database
  const getUsersData = async (id) => {
    loadingOn();
    try {
      const { data } = await API.post(
        "/device-users-list/" + id,
        { filterBy },
        {
          headers: authHeader(),
        }
      );

      if (data?.success) {
        setAllAttendanceUsers(data?.data);
        calculateUsersSummary(data?.data);
      }
      if (!data?.success) {
        dispatch(
          setNotification({
            message: data.message,
            type: "error",
          })
        );
      }

      loadingOff();
    } catch (err) {
      loadingOff();
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        })
      );
    } finally {
      loadingOff();
    }
  };

  // Calculate users summary
  const calculateUsersSummary = (users) => {
    if (!users) return;

    const adminCount = users.filter((user) => user.authorizedAdmin).length;
    setUsersSummary({
      total: users.length,
      admin: adminCount,
      user: users.length - adminCount,
    });
  };

  // Get Device Details
  const getDeviceDetails = async (id) => {
    try {
      loadingOn();
      const { data } = await API.get("/get-attendance-device/" + id, {
        headers: authHeader(),
      });

      if (data?.success) {
        setSelectedDevice(data?.data);
        setUsersLastSync(data?.data?.lastUsersSync);
        setLogsLastSync(data?.data?.lastLogsSync);
      }
      if (!data?.success) {
        dispatch(
          setNotification({
            message: data.message,
            type: "error",
          })
        );
      }
    } catch (err) {
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        })
      );
    } finally {
      loadingOff();
    }
  };

  const parsedRecordTime = (recordTime) => {
    const date = new Date(recordTime);

    // Use UTC methods to get the original time
    let hours = date.getUTCHours();
    let minutes = date.getUTCMinutes();

    const meridiem = hours >= 12 ? "PM" : "AM";
    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'

    minutes = minutes < 10 ? "0" + minutes : minutes;
    let strTime = hours + ":" + minutes + " " + meridiem;
    return strTime;
  };

  const parsedRecordDate = (recordTime) => {
    const date = new Date(recordTime);
    return date.toUTCString().slice(0, 16);
  };

  const handleUserFilterStatus = (status) => {
    // set filterBy in params
    setSearchParams((prevParams) => {
      return {
        ...prevParams,
        deviceId: searchParams.get("deviceId"),
        filterBy: status,
      };
    });
  };

  /*   All UseEffects
   ********************************************* */
  useEffect(() => {
    // If not deviceId in search params then get devices list
    let deviceId = searchParams.get("deviceId");
    if (!deviceId) {
      setShowDevicesListModal(true);
      return;
    }

    let clearTimeoutId = setTimeout(() => {
      getUsersData(deviceId);
      getDeviceDetails(deviceId);
    }, 100);
    return () => clearTimeout(clearTimeoutId);
  }, [searchParams, filterBy]);

  // set filterBy to be active by default and change filter by when params change
  useEffect(() => {
    const status = searchParams.get("filterBy");
    if (status) {
      setFilterBy(status);
    } else {
      setFilterBy("active");
    }
  }, [searchParams]);

  /*   Table functions
   ********************************************* */
  const btn1 = () => (
    <div>
      <>
        <button
          className="btn btn-primary ms-2"
          onClick={(e) => {
            setUserAddModal(true);
          }}
        >
          Add User
        </button>
      </>
    </div>
  );

  let buttons = () => (
    <div className="text-end me-1">
      <GenerateMonthlyAttendanceAllUsers
        deviceId={searchParams.get("deviceId")}
      />
      <GenerateAttendanceByDate deviceId={searchParams.get("deviceId")} />

      {/* react bootstrap Dropdown for active/inactive users */}
      <Dropdown className="d-inline-block mx-2">
        <Dropdown.Toggle variant="success" id="dropdown-basic">
          {filterBy === "active" ? "Active Users" : "Inactive Users"}
        </Dropdown.Toggle>

        <Dropdown.Menu>
          <Dropdown.Item onClick={() => handleUserFilterStatus("active")}>
            Active
          </Dropdown.Item>
          <Dropdown.Item onClick={() => handleUserFilterStatus("inactive")}>
            Inactive
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );

  const table_head = [
    "ID",
    "User Name",
    "Department",
    "Authorized admin",
    "Card No",
    "Action",
  ];

  const table_body = (item, index) => (
    <tr className="" key={item.id}>
      <td className="align-middle py-1">{item.userId}</td>
      <td className="align-middle py-1">{item.name}</td>
      <td className="align-middle py-1">{item?.department} </td>
      <td className="align-middle py-1">
        {item.authorizedAdmin ? "Admin" : "User"}
      </td>
      <td className="align-middle py-1">{item.cardno}</td>
      <td className="align-middle py-1 table-action">
        <Link
          to={`/attendance/user/${searchParams.get("deviceId")}/${
            item?.userId
          }/?filterBy=${
            searchParams.get("filterBy")
              ? `${searchParams.get("filterBy")}`
              : "active"
          }`}
          className="btn btn-sm btn-info"
        >
          Details
        </Link>
        {/* <DeleteModalButton
          deleteFunc={deleteUser}
          deleteParams={{
            id: item?.id,
            userId: item?.userId,
          }}
          btnClass={"btn btn-sm btn-danger mx-1"}
          disableConditions={loading}
        /> */}
      </td>
    </tr>
  );

  return (
    <div
      className=""
      style={{
        marginLeft: 280,
        height: "90vh",
        paddingTop: 10,
        paddingRight: 30,
      }}
    >
      {loading && <Spinner />}
      <h2 className="mb-2">Time & Attendance</h2>

      {/* Selected Device Information */}
      <div className="card mb-2">
        <div className="card-body py-2 d-flex align-items-center justify-content-between">
          <div>
            <div className="fs-5 fw-bold">
              Selected Device: {selectedDevice?.name}
            </div>
            <p className="text-muted mb-0">
              <div className="">
                IP: {selectedDevice?.ipAddress} : {selectedDevice?.port} -{" "}
                {selectedDevice?.timezone} -{" "}
                <span className="fw-bold">
                  Current Time: {getCurTimeOfDeviceTimeZone(selectedDevice)}
                </span>
              </div>
              <div className="d-flex align-items-center">
                <div className="">
                  <span className="fw-bold">Users Last Sync: </span>
                  {usersLastSync
                    ? `${parsedRecordDate(usersLastSync)} at ${parsedRecordTime(
                        usersLastSync
                      )}`
                    : "Never"}
                </div>

                <span className="ms-3">
                  <span className="fw-bold">Logs Last Sync: </span>

                  {logsLastSync
                    ? `${parsedRecordDate(logsLastSync)} at ${parsedRecordTime(
                        logsLastSync
                      )}`
                    : "Never"}
                </span>
              </div>
            </p>
          </div>
          <button
            onClick={() => setShowDevicesListModal(true)}
            className="btn btn-outline-primary btn-sm"
          >
            Change Device
          </button>
        </div>
      </div>

      <ul className="mb-2 nav nav-tabs nav-bordered border-0 d-flex flex-grow-1">
        <li className="">
          <button
            className={
              activeTab === "device"
                ? `btn btn-primary active bg-primary text-white`
                : "btn bg-white shadow-sm"
            }
            onClick={() => setActiveTab("device")}
          >
            <i className="mdi mdi-settings-outline d-md-none d-block"></i>
            <span className="d-none d-md-block">Device</span>
          </button>
        </li>
        <li className="">
          <button
            className={
              activeTab === "users"
                ? `btn btn-primary active bg-primary text-white`
                : "btn bg-white shadow-sm"
            }
            onClick={() => setActiveTab("users")}
          >
            <i className="mdi mdi-settings-outline d-md-none d-block"></i>
            <span className="d-none d-md-block">Users</span>
          </button>
        </li>
        <li className="">
          <button
            className={
              activeTab === "logs"
                ? `btn btn-primary active bg-primary text-white`
                : "btn bg-white shadow-sm"
            }
            onClick={() => setActiveTab("logs")}
          >
            <i className="mdi mdi-settings-outline d-md-none d-block"></i>
            <span className="d-none d-md-block">Logs</span>
          </button>
        </li>
      </ul>

      {activeTab === "device" && (
        <div className="mb-3">
          <AttDeviceSummary
            selectedDevice={selectedDevice}
            setSelectedDevice={setSelectedDevice}
            usersSummary={usersSummary}
            setUsersSummary={setUsersSummary}
            getDeviceDetails={getDeviceDetails}
            setAllAttendanceUsers={setAllAttendanceUsers}
            calculateUsersSummary={calculateUsersSummary}
            usersLastSync={usersLastSync}
            setUsersLastSync={setUsersLastSync}
            logsLastSync={logsLastSync}
            setLogsLastSync={setLogsLastSync}
          />
        </div>
      )}

      {activeTab === "users" && (
        <div className="mb-3">
          <div
            className="px-2 flex-grow-1 d-flex"
            style={{ height: "75vh", borderRadius: 15 }}
          >
            <div className="horizontal-scrollable d-flex flex-grow-1">
              <Table
                btn1={btn1}
                buttons={buttons}
                table_head={table_head}
                table_body={table_body}
                table_data={allAttendanceUsers}
              />
            </div>
          </div>
        </div>
      )}

      {activeTab === "logs" && (
        <div className="mb-3">
          {selectedDevice && <DeviceLogs selectedDevice={selectedDevice} />}
        </div>
      )}

      {/*   Show Devices List Modal
       ********************************************* */}
      <Modal
        show={showDevicesListModal}
        onHide={(e) => {
          setShowDevicesListModal(false);
        }}
        contentClassName="bg-transparent"
        size="xl"
      >
        <Modal.Body className="bg-white custom-border-radius custom-shadow ps-3">
          <AttSelectDevice showModal={setShowDevicesListModal} />
          <div className="text-end me-3">
            <button
              onClick={(e) => {
                setShowDevicesListModal(false);
              }}
              className="btn btn-sm btn-secondary"
            >
              Close
            </button>
          </div>
        </Modal.Body>
      </Modal>

      {/*  Add User Modal
       ********************************************* */}
      <Modal
        show={userAddModal}
        onHide={(e) => {
          setUserAddModal(false);
        }}
        contentClassName="bg-transparent"
        size="xl"
      >
        <Modal.Body className="bg-white custom-border-radius custom-shadow ps-3">
          <UpdateAttUser
            refreshFunc={getUsersData}
            showModal={setUserAddModal}
            userData={{}}
            isAdd={true}
          />
        </Modal.Body>
      </Modal>
    </div>
  );
}
